/**
 * Emperor Marble — Storefront block-code search interceptor
 * ==========================================================
 *
 * PROBLEM (verified live 2026-06-04)
 *   Searching a bare block code on the storefront — e.g.
 *   `emperormarble.co.uk/search?q=18011` (also 18010 / 18020 / 18021) —
 *   returns the WRONG product ("White Onyx Slabs"). Block codes are NOT
 *   product names; Shopify native product search is fuzzy/boosted and
 *   mis-resolves a bare code onto a boosted product. Control codes
 *   (18012, 18099, 99999) correctly return nothing, so the bad matches are
 *   a discrete enumerated set wired into Shopify Search & Discovery.
 *
 * DURABLE FIX (this file)
 *   Before Shopify's fuzzy search ever runs, classify the query:
 *     - block-code-like (single alphanumeric+hyphen token with a digit:
 *       18011, B1820, EM-CAL-001) -> resolve it against the FMM canonical
 *       inventory endpoint (exact match, NO fuzzy fallback). A known code
 *       routes the shopper to the CORRECT stone; an unknown code shows an
 *       honest "no match" instead of fuzzy-falling onto White Onyx.
 *     - everything else (stone names, free text) -> leave Shopify native
 *       product search completely untouched.
 *
 *   This remains robust even if the Search & Discovery term boosts are
 *   re-introduced later: block codes never reach Shopify's fuzzy matcher.
 *
 * SOURCE OF TRUTH
 *   `isBlockCodeLike` below is a VERBATIM port of
 *   `src/lib/shopify/search-adapter.ts` in the findmymarble-aod repo.
 *   If you change the classifier there, mirror it here (see README.md
 *   "Keeping the classifier in sync").
 *
 * ENDPOINT WIRING (Emperor Marble theme specific)
 *   The endpoint base is read at call time from `window.__FINDMYMARBLE_API_URL`
 *   (set in layout/theme.liquid from `settings.findmymarble_api_url`), so the
 *   same FMM host the rest of the theme talks to is reused. If that global is
 *   absent we fall back to the canonical production host. Path is always
 *   `/api/shopify/block-search?q=<query>`.
 *
 * DEPENDENCY-FREE
 *   Vanilla ES2017. No framework, no bundler, no imports. Drops straight
 *   into a theme asset and works in any browser the storefront supports.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  var CONFIG = {
    /**
     * Canonical production host used ONLY as a fail-safe when
     * `window.__FINDMYMARBLE_API_URL` is not set. In normal operation the base
     * comes from that global (see resolveEndpoint). CORS on the FMM endpoint
     * allow-lists emperormarble.co.uk / www.emperormarble.co.uk.
     */
    fallbackBase: 'https://findmymarble.com',

    /** Paths appended to the resolved FMM API base. */
    path: '/api/shopify/block-search',
    stoneTypesPath: '/api/shopify/stone-types',
    stoneBlocksPath: '/api/shopify/blocks',

    /**
     * What to do when a block code resolves to a known stone.
     *
     *  'redirect-to-block' (default):
     *     Resolve the stone name to a Shopify product URL, then deep-link to the
     *     exact inventory card hash rendered by the product page:
     *     18011 -> FMM says "Patagonia" -> /products/patagonia#block-<uuid>
     *     If Shopify product URL resolution fails, fail open to /search?q=<stone>.
     *
     *  'render-inline':
     *     Render a result card in #block-search-result (see renderInline()).
     *     Use when you want code-level detail (block code, dimensions, sqm)
     *     shown directly rather than bouncing to the product.
     */
    onMatch: 'redirect-to-block',

    /** Container the 'render-inline' mode writes into, and where "no match" notices go. */
    resultContainerId: 'block-search-result',

    /** Suppress native fuzzy results while a canonical block-code answer is shown. */
    nativeResultsSelector: '#ProductGridContainer, .search__form-text',

    /** Request timeout (ms). On timeout/error we FAIL OPEN to native search. */
    timeoutMs: 4000,

    /** Predictive dropdown lookup debounce (ms) for networked block-code lookups. */
    predictiveDebounceMs: 225,

    /** Mobile-first caps for dropdown inventory sections. */
    stoneResultLimit: 12,
    popularStoneLimit: 6,
    inlineBlockInitialLimit: 20,
    inlineBlockChunkSize: 20,
  };

  // SUBMIT-INTERCEPT MASTER SWITCH: when true, the search-button/Enter
  // submit path renders from the in-memory cache. On empty cache,
  // not-yet-loaded cache, zero-match free text, or any thrown error, the
  // shopper still reaches native /search: the listeners DO call
  // preventDefault() up front, and the fail-open path re-dispatches via
  // HTMLFormElement.prototype.submit() — which never re-fires the `submit`
  // event, so re-interception (and any loop) is structurally impossible.
  // Set to false to bypass interception entirely: every listener checks
  // this flag BEFORE calling preventDefault, so native search submits
  // exactly as if this SUBMIT-INTERCEPT block did not exist.
  // SCOPE: this flag governs the submit-intercept block ONLY. The older
  // block-code form interception (wireForm / autoWireForms) and the
  // predictive-dropdown patches run independently of it — turning this off
  // does NOT restore fully-native search behaviour for block codes.
  var EMPEROR_SEARCH_FAIL_OPEN = true;

  var productUrlCache = {};
  var stoneTypesCache = null;
  var stoneTypesRequest = null;
  var stoneBlocksCache = {};
  var predictiveState = {
    controller: null,
    timer: null,
    sequence: 0,
  };

  /**
   * Resolve the full endpoint base from the theme-provided global, falling back
   * to the canonical production host. Trailing slashes are trimmed so the path
   * concatenation never doubles up.
   */
  function resolveApiBase() {
    var base = (typeof window !== 'undefined' && window.__FINDMYMARBLE_API_URL)
      ? String(window.__FINDMYMARBLE_API_URL)
      : CONFIG.fallbackBase;
    return base.replace(/\/+$/, '');
  }

  function resolveApiUrl(path) {
    return resolveApiBase() + path;
  }

  function resolveEndpoint() {
    return resolveApiUrl(CONFIG.path);
  }

  // ---------------------------------------------------------------------------
  // Classifier — VERBATIM from src/lib/shopify/search-adapter.ts
  // ---------------------------------------------------------------------------

  /**
   * Decide whether a raw search query looks like a block code.
   *
   * Block codes are short identifiers that contain at least one digit and may
   * carry ONE internal separator between a prefix and a number — e.g. `18011`,
   * `B1820`, `EM-CAL-001`, and crucially `SG 13508` (the real on-product format
   * a shopper reads and types). Stone names / product text are rejected because
   * they are purely alphabetic (`White Onyx`, `Patagonia`) or too long.
   *
   * VERBATIM from src/lib/shopify/search-adapter.ts — keep in sync.
   */
  function isBlockCodeLike(raw) {
    var q = (raw == null ? '' : String(raw)).trim();
    if (!q) return false;
    // A block code MUST carry at least one digit (stone names are alphabetic).
    if (!/\d/.test(q)) return false;
    // Identifiers are short; reject long free text even if it contains a number.
    if (q.length > 24) return false;
    // Allowed shape: alphanumeric tokens joined by single separators (space or
    // hyphen): 18011, B1820, EM-CAL-001, SG 13508.
    return /^[A-Za-z0-9]+(?:[\s-][A-Za-z0-9]+)*$/.test(q);
  }

  // ---------------------------------------------------------------------------
  // FMM canonical lookup
  // ---------------------------------------------------------------------------

  /**
   * Resolve a block-code-like query against the FMM canonical inventory.
   * Returns the parsed ShopifyBlockSearchResponse, or null on network failure.
   */
  function lookupBlockCode(query, options) {
    options = options || {};
    var url = resolveEndpoint() + '?q=' + encodeURIComponent(query);
    var controller = !options.signal && typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, CONFIG.timeoutMs)
      : null;

    return fetch(url, {
      method: 'GET',
      // The endpoint is CORS-allow-listed for this origin; no credentials needed.
      headers: { Accept: 'application/json' },
      signal: options.signal || (controller ? controller.signal : undefined),
    })
      .then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        return null; // FAIL OPEN — caller falls through to native search.
      });
  }

  /**
   * fetch with the same AbortController + CONFIG.timeoutMs discipline as
   * lookupBlockCode. Without this cap a hung (not refused) connection keeps the
   * submit path pending for 10-30+ s with no indicator — a dead-looking search
   * box (measured against the 503'ing backend, 2026-08-10 preview test).
   */
  function timedFetch(url) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, CONFIG.timeoutMs)
      : null;

    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    }).then(
      function (res) {
        if (timer) clearTimeout(timer);
        return res;
      },
      function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      }
    );
  }

  function normalizeInventoryTerm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function normalizeStoneSearchTerm(value) {
    var text = String(value == null ? '' : value);
    if (typeof text.normalize === 'function') text = text.normalize('NFD');
    return text
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function stoneSearchTokens(term) {
    return term ? term.split(' ').filter(Boolean) : [];
  }

  function levenshteinDistance(a, b) {
    if (a === b) return 0;
    if (!a) return b.length;
    if (!b) return a.length;

    var previous = [];
    var current = [];
    for (var i = 0; i <= b.length; i++) previous[i] = i;

    for (var j = 1; j <= a.length; j++) {
      current[0] = j;
      for (var k = 1; k <= b.length; k++) {
        var cost = a.charAt(j - 1) === b.charAt(k - 1) ? 0 : 1;
        current[k] = Math.min(
          current[k - 1] + 1,
          previous[k] + 1,
          previous[k - 1] + cost
        );
      }
      var swap = previous;
      previous = current;
      current = swap;
    }

    return previous[b.length];
  }

  function fuzzyStoneThreshold(term) {
    return Math.max(1, Math.min(3, Math.floor(term.length / 4)));
  }

  function fuzzyStoneDistance(queryTerm, nameTerm) {
    if (!queryTerm || queryTerm.length < 4 || !nameTerm) return null;
    var threshold = fuzzyStoneThreshold(queryTerm);
    var candidates = [nameTerm].concat(stoneSearchTokens(nameTerm));
    var best = null;

    for (var i = 0; i < candidates.length; i++) {
      var candidate = candidates[i];
      if (!candidate) continue;
      if (Math.abs(candidate.length - queryTerm.length) > threshold) continue;
      var distance = levenshteinDistance(queryTerm, candidate);
      if (distance <= threshold && (best === null || distance < best)) best = distance;
    }

    return best;
  }

  function hasAllStoneTokens(nameTerm, queryTokens) {
    if (!nameTerm || queryTokens.length < 2) return false;
    for (var i = 0; i < queryTokens.length; i++) {
      if (nameTerm.indexOf(queryTokens[i]) === -1) return false;
    }
    return true;
  }

  function readStoneTypes(data) {
    var stones = data && data.stone_types && data.stone_types.length ? data.stone_types : [];
    return stones.filter(function (stone) {
      return stone && stone.stone_name;
    });
  }

  function ensureStoneTypes() {
    if (stoneTypesCache) return Promise.resolve(stoneTypesCache);
    if (stoneTypesRequest) return stoneTypesRequest;

    stoneTypesRequest = timedFetch(resolveApiUrl(CONFIG.stoneTypesPath))
      .then(function (res) {
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (data) {
        stoneTypesCache = readStoneTypes(data);
        return stoneTypesCache;
      })
      .catch(function () {
        stoneTypesCache = [];
        return stoneTypesCache;
      });

    return stoneTypesRequest;
  }

  function lookupBlocksForStone(stoneName) {
    var stone = String(stoneName || '').trim();
    var cacheKey = normalizeInventoryTerm(stone);
    if (!stone) return Promise.resolve([]);
    if (stoneBlocksCache[cacheKey]) return stoneBlocksCache[cacheKey];

    stoneBlocksCache[cacheKey] = timedFetch(
      resolveApiUrl(CONFIG.stoneBlocksPath) + '?stone_name=' + encodeURIComponent(stone)
    )
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.blocks) return [];
        return uniqueBlocks(data.blocks);
      })
      .catch(function () {
        delete stoneBlocksCache[cacheKey];
        return null;
      });

    return stoneBlocksCache[cacheKey];
  }

  // ---------------------------------------------------------------------------
  // ISOLATION SEAM — single chokepoint for candidate results
  // ---------------------------------------------------------------------------
  //
  // TODAY this seam is the entry point of the SUBMIT path ONLY (search button /
  // Enter key). The predictive dropdown does NOT read through it yet: it still
  // walks filterStoneTypes / lookupBlocksForStone / lookupBlockCode inline from
  // runPredictiveStoneSearch and runPredictiveBlockSearch. The seam wraps those
  // same primitives, so nothing is duplicated at the loader level, but the two
  // callers are not yet unified — folding the dropdown's inline walk into this
  // function is future work, not a property of the current code.

  // FUTURE SEAM — swap the per-stone stoneBlocksCache walk for a single bulk-catalog source here later;
  // do NOT add a network endpoint in this function.
  function getCandidateResultsForQuery(query) {
    var q = (query == null ? '' : String(query)).trim();
    if (!q) return Promise.resolve({ stones: [], blocks: [], mode: 'empty' });

    // Block-code queries: use the existing block-code classifier path
    // (does NOT touch the per-stone loader — the FMM endpoint knows the
    // answer in one round-trip and the submit path will hand it off to
    // the existing handleMatch / renderBlockList routes).
    if (isBlockCodeLike(q)) {
      return lookupBlockCode(q).then(function (data) {
        if (!data) return { stones: [], blocks: [], mode: 'block-code-unresolved' };
        // The server re-applies the same classifier; honour its decision (mirrors
        // interceptSearch — 'block-code-unresolved' already fails open to native).
        if (data.route === 'product-search') return { stones: [], blocks: [], mode: 'block-code-unresolved' };
        var blocks = uniqueBlocks(data.blocks || []);
        return { stones: [], blocks: blocks, mode: 'block-code', data: data };
      });
    }

    // Free-text / stone-name queries: reuse the existing stone matcher
    // (filterStoneTypes) over the already-loaded stoneTypesCache. The
    // dropdown still does this walk inline itself (see the seam banner
    // above) — this is the submit path's copy of that walk, sharing the
    // underlying matcher/loader functions, not yet a single caller.
    return ensureStoneTypes().then(function (stones) {
      if (!stones || !stones.length) {
        return { stones: [], blocks: [], mode: 'stones-empty' };
      }
      var matches = filterStoneTypes(q);
      // For each surviving stone, await the EXISTING per-stone block loader
      // if that stone's blocks are not yet in stoneBlocksCache. We do NOT
      // write a new loader — lookupBlocksForStone IS the loader.
      var blockPromises = matches.map(function (stone) {
        var name = stone && stone.stone_name ? stone.stone_name : '';
        if (!name) return Promise.resolve([]);
        return lookupBlocksForStone(name).then(function (blocks) {
          return blocks || [];
        });
      });
      return Promise.all(blockPromises).then(function (perStoneBlocks) {
        var flat = [];
        for (var i = 0; i < perStoneBlocks.length; i++) {
          for (var j = 0; j < perStoneBlocks[i].length; j++) flat.push(perStoneBlocks[i][j]);
        }
        return { stones: matches, blocks: flat, mode: 'stones' };
      });
    });
  }

  function filterStoneTypes(query) {
    var q = normalizeStoneSearchTerm(query);
    if (!q || !stoneTypesCache || !stoneTypesCache.length) return [];
    var queryTokens = stoneSearchTokens(q);
    var exactMatches = [];
    var prefixMatches = [];
    var substringMatches = [];
    var tokenMatches = [];
    var fuzzyMatches = [];

    for (var i = 0; i < stoneTypesCache.length; i++) {
      var stone = stoneTypesCache[i];
      var name = normalizeStoneSearchTerm(stone.stone_name);
      var category = normalizeStoneSearchTerm(stone.stone_category);
      if (!name && !category) continue;
      if (name === q) {
        exactMatches.push(stone);
      } else if (name.indexOf(q) === 0) {
        prefixMatches.push(stone);
      } else if (name.indexOf(q) !== -1 || category.indexOf(q) !== -1) {
        substringMatches.push(stone);
      } else if (hasAllStoneTokens(name, queryTokens)) {
        tokenMatches.push(stone);
      } else {
        var distance = fuzzyStoneDistance(q, name);
        if (distance !== null) fuzzyMatches.push({ stone: stone, distance: distance, name: name });
      }
    }

    fuzzyMatches.sort(function (a, b) {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    });

    return exactMatches
      .concat(prefixMatches, substringMatches, tokenMatches)
      .concat(
        fuzzyMatches.map(function (match) {
          return match.stone;
        })
      )
      .slice(0, CONFIG.stoneResultLimit);
  }

  // ---------------------------------------------------------------------------
  // Outcomes
  // ---------------------------------------------------------------------------

  function getContainer() {
    return document.getElementById(CONFIG.resultContainerId);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fallbackStoneSearchUrl(stoneName) {
    return '/search?q=' + encodeURIComponent(stoneName || '');
  }

  function blockHash(block) {
    return block && block.id ? '#block-' + encodeURIComponent(block.id) : '';
  }

  function appendHash(url, hash) {
    if (!url || !hash) return url;
    return url.split('#')[0] + hash;
  }

  function normalizeTitle(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function readSuggestProducts(data) {
    return (((data || {}).resources || {}).results || {}).products || [];
  }

  function resolveProductUrl(stoneName, options) {
    var stone = String(stoneName || '').trim();
    var cacheKey = normalizeTitle(stone);
    if (!stone) return Promise.resolve(null);
    if (productUrlCache[cacheKey]) return productUrlCache[cacheKey];

    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(stone) +
      '&resources[type]=product&resources[limit]=3';

    productUrlCache[cacheKey] = fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options && options.signal ? options.signal : undefined,
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (data) {
        var products = readSuggestProducts(data);
        if (!products.length) return null;
        var wanted = normalizeTitle(stone);
        var chosen = products[0];
        for (var i = 0; i < products.length; i++) {
          if (normalizeTitle(products[i].title) === wanted) {
            chosen = products[i];
            break;
          }
        }
        return chosen && chosen.url ? chosen.url : null;
      })
      .then(function (productUrl) {
        if (!productUrl) delete productUrlCache[cacheKey];
        return productUrl;
      })
      .catch(function () {
        delete productUrlCache[cacheKey];
        return null;
      });

    return productUrlCache[cacheKey];
  }

  function resolveBlockUrl(block, query, options) {
    var stone = block && block.stone_name ? block.stone_name : query;
    return resolveProductUrl(stone, options).then(function (productUrl) {
      if (!productUrl) return fallbackStoneSearchUrl(stone);
      return appendHash(productUrl, blockHash(block));
    });
  }

  function uniqueBlocks(blocks) {
    var seen = {};
    var out = [];
    for (var i = 0; i < (blocks || []).length; i++) {
      var block = blocks[i];
      var key = block && (block.id || block.block_code || i);
      if (seen[key]) continue;
      seen[key] = true;
      out.push(block);
    }
    return out;
  }

  /** A known block code resolved to a stone — route the shopper to it. */
  function handleMatch(query, block) {
    if (CONFIG.onMatch === 'render-inline') {
      renderInline(query, block);
      return;
    }
    resolveBlockUrl(block, query).then(function (url) {
      window.location.assign(url);
    });
  }

  /** Render a minimal result card (only used when onMatch === 'render-inline'). */
  function renderInline(query, block) {
    var el = getContainer();
    if (!el) {
      // No container wired — fall back to the safe redirect so the shopper
      // still lands on the right product.
      resolveBlockUrl(block, query).then(function (url) {
        window.location.assign(url);
      });
      return;
    }
    renderBlockList(el, query, [block]);
  }

  /**
   * A block-code-like query with NO canonical match. This is the critical
   * case: we MUST show an honest "no result" and must NOT let Shopify fuzzy
   * search take over (that is exactly the 18010 -> White Onyx bug).
   */
  function handleNoMatch(query) {
    var el = getContainer() || createResultsPageContainer();
    if (el) {
      renderNoMatch(el, query);
    }
    suppressNativeResults();
  }

  function suppressNativeResults() {
    if (!CONFIG.nativeResultsSelector) return;
    var nodes = document.querySelectorAll(CONFIG.nativeResultsSelector);
    for (var i = 0; i < nodes.length; i++) nodes[i].hidden = true;
  }

  function createResultsPageContainer() {
    var el = getContainer();
    if (el) return el;
    el = document.createElement('div');
    el.id = CONFIG.resultContainerId;
    el.hidden = true;
    var target = document.getElementById('ProductGridContainer') || document.querySelector('.template-search__header');
    if (target && target.parentNode) {
      target.parentNode.insertBefore(el, target);
    } else {
      document.body.appendChild(el);
    }
    return el;
  }

  function blockImage(block) {
    return block && block.images && block.images[0] ? block.images[0] : '';
  }

  function formatInventoryNumber(value) {
    if (value == null || value === '') return '';
    var number = Number(value);
    if (!isNaN(number)) {
      return number.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return String(value);
  }

  function inventoryMetric(value) {
    var number = Number(value);
    return isNaN(number) ? 0 : number;
  }

  function popularStoneTypes() {
    if (!stoneTypesCache || !stoneTypesCache.length) return [];
    return stoneTypesCache
      .slice()
      .sort(function (a, b) {
        var blockDelta = inventoryMetric(b.block_count) - inventoryMetric(a.block_count);
        if (blockDelta) return blockDelta;
        var sqmDelta = inventoryMetric(b.total_available_sqm) - inventoryMetric(a.total_available_sqm);
        if (sqmDelta) return sqmDelta;
        var aName = normalizeStoneSearchTerm(a.stone_name);
        var bName = normalizeStoneSearchTerm(b.stone_name);
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      })
      .slice(0, CONFIG.popularStoneLimit);
  }

  function stoneMetaMarkup(stone) {
    var parts = [];
    if (stone && stone.stone_category) parts.push(escapeHtml(stone.stone_category));
    if (stone && stone.block_count != null) {
      parts.push(escapeHtml(formatInventoryNumber(stone.block_count)) + ' blocks');
    }
    if (stone && stone.total_available_sqm != null) {
      parts.push(escapeHtml(formatInventoryNumber(stone.total_available_sqm)) + ' m²');
    }
    return parts.join(' · ');
  }

  function stoneRowMarkup(stone) {
    var name = stone && stone.stone_name ? stone.stone_name : '';
    var meta = stoneMetaMarkup(stone);
    return (
      '<a class="block-search-stone" href="' +
      escapeHtml(fallbackStoneSearchUrl(name)) +
      '" data-block-search-stone="' +
      escapeHtml(name) +
      '">' +
      '<span class="block-search-stone__body">' +
      '<span class="block-search-stone__name">' +
      escapeHtml(name) +
      '</span>' +
      (meta ? '<span class="block-search-stone__meta">' + meta + '</span>' : '') +
      '</span>' +
      '</a>'
    );
  }

  function blockCardMarkup(block, href) {
    var img = blockImage(block);
    var code = block && block.block_code ? block.block_code : '';
    var stone = block && block.stone_name ? block.stone_name : '';
    var sqm = block && block.available_sqm != null ? block.available_sqm : null;
    return (
      '<a class="block-search-card" href="' +
      escapeHtml(href) +
      '" data-block-code="' +
      escapeHtml(code) +
      '">' +
      (img
        ? '<img class="block-search-card__img" src="' +
          escapeHtml(img) +
          '" alt="' +
          escapeHtml(stone) +
          '" loading="lazy">'
        : '<span class="block-search-card__img block-search-card__img--empty" aria-hidden="true"></span>') +
      '<span class="block-search-card__body">' +
      '<span class="block-search-card__code">Block ' +
      escapeHtml(code) +
      '</span>' +
      '<span class="block-search-card__stone">' +
      escapeHtml(stone) +
      '</span>' +
      (sqm != null
        ? '<span class="block-search-card__sqm">' + escapeHtml(sqm) + ' m² available</span>'
        : '') +
      '</span>' +
      '</a>'
    );
  }

  function sectionMarkup(title, body, extraClass) {
    return (
      '<section class="block-search-section' +
      (extraClass ? ' ' + escapeHtml(extraClass) : '') +
      '">' +
      '<h3 class="block-search-heading">' +
      escapeHtml(title) +
      '</h3>' +
      body +
      '</section>'
    );
  }

  function renderPredictiveStoneResults(instance, query) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    var matches = filterStoneTypes(query);
    if (!matches.length) {
      panel.innerHTML = '';
      panel.hidden = true;
      hidePredictiveNativeResults(instance, false);
      return;
    }

    var limit = panel.__emperorShowAllStones ? matches.length : CONFIG.stoneResultLimit;
    var visibleMatches = matches.slice(0, limit);
    var rows = visibleMatches.map(stoneRowMarkup).join('');
    var moreButton =
      matches.length > visibleMatches.length
        ? '<button class="block-search-more" type="button" data-block-search-show-all-stones>Show all ' +
          escapeHtml(formatInventoryNumber(matches.length)) +
          ' stones</button>'
        : '';

    panel.innerHTML = sectionMarkup(
      'Stones',
      '<div class="block-search-stones" role="list">' + rows + '</div>' + moreButton,
      'block-search-section--stones'
    );
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(
      instance,
      matches.length === 1 ? '1 stone match found.' : matches.length + ' stone matches found.'
    );
  }

  function renderPopularStoneRecommendations(instance) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    var stones = popularStoneTypes();
    if (!stones.length) {
      panel.innerHTML = '';
      panel.hidden = true;
      hidePredictiveNativeResults(instance, false);
      return;
    }

    panel.__emperorShowAllStones = false;
    panel.innerHTML = sectionMarkup(
      'Popular stones',
      '<div class="block-search-stones" role="list">' + stones.map(stoneRowMarkup).join('') + '</div>',
      'block-search-section--stones block-search-section--popular'
    );
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(
      instance,
      stones.length === 1 ? '1 popular stone available.' : stones.length + ' popular stones available.'
    );
  }

  function renderPredictiveStoneLoading(instance) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    panel.innerHTML = '<p class="block-search-status">Loading stones…</p>';
    panel.hidden = false;
    hidePredictiveNativeResults(instance, false);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(instance, 'Loading stone inventory…');
  }

  function renderBlocksStatus(instance, message) {
    var panel = getPredictivePanel(instance);
    if (!panel) return;
    panel.innerHTML =
      '<div class="block-search-blocks-view">' +
      '<button class="block-search-back" type="button" data-block-search-back>Back to results</button>' +
      '<p class="block-search-status">' +
      escapeHtml(message) +
      '</p>' +
      '</div>';
    panel.hidden = false;
    if (typeof instance.open === 'function') instance.open();
  }

  function renderInlineStoneBlocks(instance, stoneName, blocks, options) {
    options = options || {};
    var panel = getPredictivePanel(instance);
    if (!panel) return Promise.resolve(0);
    var safeBlocks = uniqueBlocks(blocks);
    var requestedLimit = options.visibleLimit || CONFIG.inlineBlockInitialLimit;
    var shown = Math.min(safeBlocks.length, requestedLimit);
    var visibleBlocks = safeBlocks.slice(0, shown);

    if (!safeBlocks.length) {
      renderBlocksStatus(instance, 'No blocks available for “' + stoneName + '”.');
      announcePredictive(instance, 'No blocks available for ' + stoneName + '.');
      return Promise.resolve(0);
    }

    return resolveProductUrl(stoneName).then(function (productUrl) {
      if (options.sequence !== predictiveState.sequence) return 0;
      var baseUrl = productUrl || fallbackStoneSearchUrl(stoneName);
      var cards = visibleBlocks
        .map(function (block) {
          return blockCardMarkup(block, productUrl ? appendHash(baseUrl, blockHash(block)) : baseUrl);
        })
        .join('');
      var moreButton =
        safeBlocks.length > visibleBlocks.length
          ? '<button class="block-search-more" type="button" data-block-search-show-all-blocks="' +
            escapeHtml(stoneName) +
            '" data-block-search-block-limit="' +
            escapeHtml(Math.min(safeBlocks.length, visibleBlocks.length + CONFIG.inlineBlockChunkSize)) +
            '">Show ' +
            escapeHtml(formatInventoryNumber(Math.min(CONFIG.inlineBlockChunkSize, safeBlocks.length - visibleBlocks.length))) +
            ' more (' +
            escapeHtml(formatInventoryNumber(safeBlocks.length)) +
            ' total)</button>'
          : '';

      panel.innerHTML =
        '<div class="block-search-blocks-view">' +
        '<div class="block-search-blocks-view__header">' +
        '<button class="block-search-back" type="button" data-block-search-back>Back to results</button>' +
        '<span class="block-search-blocks-view__title">' +
        escapeHtml(stoneName) +
        '</span>' +
        '</div>' +
        sectionMarkup(
          'Blocks',
          '<div class="block-search-panel block-search-panel--scroll" role="list">' + cards + '</div>' + moreButton,
          'block-search-section--blocks'
        ) +
        '</div>';
      panel.hidden = false;
      if (typeof instance.open === 'function') instance.open();
      announcePredictive(
        instance,
        safeBlocks.length === 1 ? '1 block found for ' + stoneName + '.' : safeBlocks.length + ' blocks found for ' + stoneName + '.'
      );
      return visibleBlocks.length;
    });
  }

  function renderBlockList(el, query, blocks, options) {
    options = options || {};
    var safeBlocks = uniqueBlocks(blocks);
    return Promise.all(
      safeBlocks.map(function (block) {
        return resolveBlockUrl(block, query, options).then(function (href) {
          return blockCardMarkup(block, href);
        });
      })
    ).then(function (cards) {
      if (options.signal && options.signal.aborted) return;
      var body =
        '<div class="block-search-panel" role="group" aria-label="Block code matches">' +
        cards.join('') +
        '</div>';
      el.innerHTML = options.heading ? sectionMarkup(options.heading, body, 'block-search-section--blocks') : body;
      el.hidden = false;
      return safeBlocks.length;
    });
  }

  function renderNoMatch(el, query) {
    el.innerHTML =
      '<p class="block-search-empty">No stone matches block code “' +
      escapeHtml(query) +
      '”.</p>';
    el.hidden = false;
  }

  function renderChecking(el) {
    el.innerHTML = '<p class="block-search-status">Checking block code…</p>';
    el.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Public entrypoint
  // ---------------------------------------------------------------------------

  /**
   * Intercept a search query. Returns a Promise<boolean>:
   *   true  -> we handled it (block-code path); native search should NOT run.
   *   false -> not a block code (or lookup failed); let native search proceed.
   *
   * Wire this BEFORE Shopify's native search executes (see README.md).
   */
  function interceptSearch(rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!isBlockCodeLike(query)) {
      return Promise.resolve(false); // free text -> native product search
    }
    return lookupBlockCode(query).then(function (data) {
      // Network failure -> fail open to native search rather than block the shopper.
      if (!data) return false;
      // The server re-applies the same classifier; honour its decision.
      if (data.route === 'product-search') return false;
      var blocks = uniqueBlocks(data.blocks || []);
      if (data.matched && blocks.length === 1) {
        handleMatch(query, blocks[0]);
      } else if (data.matched && blocks.length > 1) {
        renderBlockList(createResultsPageContainer(), query, blocks);
      } else {
        handleNoMatch(query);
      }
      return true; // handled — native search must not fuzzy-fall onto a product
    });
  }

  function getPredictivePanel(instance) {
    var root = instance && instance.predictiveSearchResults;
    if (!root) return null;
    // CRITICAL: the panel must live OUTSIDE `predictiveSearchResults`. The native
    // component renders results via `predictiveSearchResults.innerHTML = markup`,
    // which DETACHES any panel placed inside it — so an async (block-code) card
    // that resolves after a native re-render would write into an orphaned node and
    // never appear (observed live: typing a block code showed nothing while a
    // direct render worked). Anchor the panel as a sibling just BEFORE the native
    // results, inside their shared parent, so it survives every native re-render.
    var host = root.parentNode || root;
    var panel = host.querySelector('[data-block-search-predictive]');
    if (!panel) {
      panel = document.createElement('div');
      panel.setAttribute('data-block-search-predictive', '');
      host.insertBefore(panel, root);
    }
    if (!panel.__emperorBlockSearchEvents) {
      panel.__emperorBlockSearchEvents = true;
      panel.addEventListener('click', function (event) {
        handlePredictivePanelClick(instance, event);
      });
    }
    return panel;
  }

  function hidePredictiveNativeResults(instance, hide) {
    if (!instance || !instance.predictiveSearchResults) return;
    var selectors = [
      '#predictive-search-results-groups-wrapper',
      '.predictive-search__text',
      '[data-predictive-search-live-region-count-value]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var nodes = instance.predictiveSearchResults.querySelectorAll(selectors[i]);
      for (var j = 0; j < nodes.length; j++) nodes[j].hidden = !!hide;
    }
  }

  function announcePredictive(instance, text) {
    if (instance && typeof instance.setLiveRegionText === 'function') {
      instance.setLiveRegionText(text);
    }
  }

  function nextPredictiveSequence() {
    predictiveState.sequence += 1;
    return predictiveState.sequence;
  }

  function stopPredictiveBlockLookup() {
    if (predictiveState.timer) clearTimeout(predictiveState.timer);
    if (predictiveState.controller) predictiveState.controller.abort();
    predictiveState.timer = null;
    predictiveState.controller = null;
  }

  function clearPredictiveInventoryResults(instance) {
    nextPredictiveSequence();
    stopPredictiveBlockLookup();
    var panel = getPredictivePanel(instance);
    if (panel) {
      panel.innerHTML = '';
      panel.hidden = true;
      panel.__emperorShowAllStones = false;
    }
    hidePredictiveNativeResults(instance, false);
  }

  function runPredictivePopularStones(instance) {
    var panel = getPredictivePanel(instance);
    if (panel) panel.__emperorShowAllStones = false;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();

    if (stoneTypesCache) {
      renderPopularStoneRecommendations(instance);
      return;
    }

    renderPredictiveStoneLoading(instance);
    ensureStoneTypes().then(function () {
      if (sequence !== predictiveState.sequence) return;
      if (normalizeInventoryTerm(instance.getQuery ? instance.getQuery() : '')) return;
      renderPopularStoneRecommendations(instance);
    });
  }

  function runPredictiveStoneSearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!query || isBlockCodeLike(query)) return;

    var panel = getPredictivePanel(instance);
    if (panel) panel.__emperorShowAllStones = false;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();

    if (stoneTypesCache) {
      renderPredictiveStoneResults(instance, query);
      return;
    }

    renderPredictiveStoneLoading(instance);
    ensureStoneTypes().then(function () {
      if (sequence !== predictiveState.sequence) return;
      if ((instance.getQuery ? instance.getQuery() : query) !== query) return;
      renderPredictiveStoneResults(instance, query);
    });
  }

  function runPredictiveBlockSearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!isBlockCodeLike(query)) return;

    var sequence = nextPredictiveSequence();
    if (predictiveState.timer) clearTimeout(predictiveState.timer);
    if (predictiveState.controller) predictiveState.controller.abort();
    predictiveState.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    var panel = getPredictivePanel(instance);
    if (!panel) return;
    renderChecking(panel);
    hidePredictiveNativeResults(instance, true);
    if (typeof instance.open === 'function') instance.open();
    announcePredictive(instance, 'Checking block code…');

    predictiveState.timer = setTimeout(function () {
      var controller = predictiveState.controller;
      lookupBlockCode(query, { signal: controller ? controller.signal : undefined }).then(function (data) {
        if (controller && controller.signal.aborted) return;
        if (sequence !== predictiveState.sequence) return;
        if (!isBlockCodeLike(instance.getQuery ? instance.getQuery() : query)) return;
        if (!data) {
          panel.innerHTML = '';
          panel.hidden = true;
          hidePredictiveNativeResults(instance, false);
          return;
        }
        if (data.route === 'product-search') {
          clearPredictiveInventoryResults(instance);
          return;
        }
        var blocks = uniqueBlocks(data.blocks || []);
        if (data.matched && blocks.length > 0) {
          hidePredictiveNativeResults(instance, true);
          renderBlockList(panel, query, blocks, {
            signal: controller ? controller.signal : undefined,
            heading: 'Blocks',
          }).then(function (count) {
            if (!count) return;
            announcePredictive(
              instance,
              count === 1 ? '1 block code match found.' : count + ' block code matches found.'
            );
          });
        } else {
          hidePredictiveNativeResults(instance, true);
          renderNoMatch(panel, query);
          announcePredictive(instance, 'No block code matches found.');
        }
        if (typeof instance.open === 'function') instance.open();
      });
    }, CONFIG.predictiveDebounceMs);
  }

  function runPredictiveInventorySearch(instance, rawQuery) {
    var query = (rawQuery == null ? '' : String(rawQuery)).trim();
    if (!query) {
      runPredictivePopularStones(instance);
      return;
    }
    if (isBlockCodeLike(query)) {
      runPredictiveBlockSearch(instance, query);
    } else {
      runPredictiveStoneSearch(instance, query);
    }
  }

  function expandPredictiveStone(instance, stoneName, options) {
    options = options || {};
    var stone = String(stoneName || '').trim();
    if (!stone) return;
    stopPredictiveBlockLookup();
    var sequence = nextPredictiveSequence();
    renderBlocksStatus(instance, 'Loading blocks…');
    announcePredictive(instance, 'Loading blocks for ' + stone + '…');

    lookupBlocksForStone(stone).then(function (blocks) {
      if (sequence !== predictiveState.sequence) return;
      if (blocks === null) {
        renderBlocksStatus(instance, 'Unable to load blocks for “' + stone + '”.');
        announcePredictive(instance, 'Unable to load blocks for ' + stone + '.');
        return;
      }
      renderInlineStoneBlocks(instance, stone, blocks, {
        sequence: sequence,
        visibleLimit: options.visibleLimit || CONFIG.inlineBlockInitialLimit,
      });
    });
  }

  function handlePredictivePanelClick(instance, event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var stoneLink = target.closest('[data-block-search-stone]');
    if (stoneLink) {
      event.preventDefault();
      expandPredictiveStone(instance, stoneLink.getAttribute('data-block-search-stone'));
      return;
    }

    var backButton = target.closest('[data-block-search-back]');
    if (backButton) {
      event.preventDefault();
      runPredictiveInventorySearch(instance, instance.getQuery ? instance.getQuery() : '');
      return;
    }

    var showAllStones = target.closest('[data-block-search-show-all-stones]');
    if (showAllStones) {
      event.preventDefault();
      var panel = getPredictivePanel(instance);
      if (panel) panel.__emperorShowAllStones = true;
      renderPredictiveStoneResults(instance, instance.getQuery ? instance.getQuery() : '');
      return;
    }

    var showAllBlocks = target.closest('[data-block-search-show-all-blocks]');
    if (showAllBlocks) {
      event.preventDefault();
      expandPredictiveStone(instance, showAllBlocks.getAttribute('data-block-search-show-all-blocks'), {
        visibleLimit: Number(showAllBlocks.getAttribute('data-block-search-block-limit')) || CONFIG.inlineBlockInitialLimit,
      });
    }
  }

  // Expose for the theme to call, and for testing.
  window.EmperorBlockSearch = {
    isBlockCodeLike: isBlockCodeLike,
    interceptSearch: interceptSearch,
    lookupBlockCode: lookupBlockCode,
    ensureStoneTypes: ensureStoneTypes,
    filterStoneTypes: filterStoneTypes,
    lookupBlocksForStone: lookupBlocksForStone,
    resolveProductUrl: resolveProductUrl,
    resolveBlockUrl: resolveBlockUrl,
    renderPredictiveBlockSearch: runPredictiveInventorySearch,
    renderPredictiveInventorySearch: runPredictiveInventorySearch,
    config: CONFIG,
  };

  // ---------------------------------------------------------------------------
  // Auto-wiring (search RESULTS page)
  // ---------------------------------------------------------------------------
  //
  // On the search results page (/search?q=...), if the query is a block code
  // we take over before the shopper reads the (wrong) fuzzy results. This runs
  // as soon as the script loads. For the search-AS-YOU-TYPE box, wire
  // interceptSearch() on submit instead — see README.md.

  function autoWireResultsPage() {
    if (!/\/search\b/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    if (!isBlockCodeLike(q)) return; // native search already correct for text
    // Optionally hide the wrong native results immediately while we resolve.
    suppressNativeResults();
    interceptSearch(q).then(function (handled) {
      // If not handled (failed open), native results remain — unhide if hidden.
      if (!handled && CONFIG.nativeResultsSelector) {
        var nodes = document.querySelectorAll(CONFIG.nativeResultsSelector);
        for (var i = 0; i < nodes.length; i++) nodes[i].hidden = false;
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Auto-wiring (search FORM submit)
  // ---------------------------------------------------------------------------
  //
  // Intercept on submit so a block-code query is resolved BEFORE the browser
  // navigates to the (wrong) native /search results. The results-page form
  // carries `data-block-search`; predictive header/template forms are covered
  // by the custom element selector. We FAIL OPEN: free text submits natively,
  // and any lookup error lets the original submit proceed.

  function isEmperorInlineSearchForm(form) {
    return Boolean(
      form &&
      (form.matches('[data-em-inline-search-form]') || form.closest('em-inline-search'))
    );
  }

  function wireForm(form) {
    if (!form || isEmperorInlineSearchForm(form) || form.__emperorBlockSearchWired) return;
    form.__emperorBlockSearchWired = true;
    form.addEventListener('submit', function (e) {
      var input = form.querySelector('input[name="q"]');
      var q = input ? String(input.value || '').trim() : '';
      if (!isBlockCodeLike(q)) return; // free text -> let native search submit
      e.preventDefault();
      interceptSearch(q).then(function (handled) {
        // Failed open (network error / not a block code) -> native search.
        if (!handled) form.submit();
      });
    });
  }

  function autoWireForms() {
    var forms = document.querySelectorAll('form[data-block-search], predictive-search form[action*="/search"]');
    for (var i = 0; i < forms.length; i++) {
      if (!isEmperorInlineSearchForm(forms[i])) wireForm(forms[i]);
    }
  }

  // ---------------------------------------------------------------------------
  // SUBMIT INTERCEPT — search BUTTON / Enter render from in-memory cache
  // ---------------------------------------------------------------------------
  //
  // The predictive dropdown already renders from stoneTypesCache +
  // stoneBlocksCache. Pressing the search button or Enter previously navigated
  // to Shopify's native /search?q=... page (~537KB, ~1.3s). This block keeps
  // the page put and renders results from the SAME in-memory cache, instantly.
  //
  // All three listeners route through the single seam
  // `getCandidateResultsForQuery` (defined above). NOTE: that seam is shared by
  // these three submit listeners only — the predictive dropdown still runs its
  // own inline candidate walk. The seam and the dropdown call the same
  // underlying loaders (filterStoneTypes / lookupBlocksForStone /
  // lookupBlockCode); unifying the two callers on one walk is future work.
  //
  // FAIL-OPEN contract (see EMPEROR_SEARCH_FAIL_OPEN at the top of the IIFE):
  //   empty cache         -> native submit proceeds
  //   not-yet-loaded      -> native submit proceeds
  //   empty query         -> native submit proceeds
  //   free text, 0 match  -> native submit proceeds (native /search also
  //                          covers products/pages/articles beyond stones)
  //   any thrown error    -> native submit proceeds
  //   block-code no-match -> honest "no match" panel (the 18010 fix) — the
  //                          ONE case that must NOT fall through to fuzzy.
  // Re-dispatch is via HTMLFormElement.prototype.submit() (never
  // requestSubmit), so the submit event is not re-fired and the listeners
  // cannot re-enter. The kill-switch is checked BEFORE preventDefault in
  // every listener. We never blank or hard-fail the search box.

  /**
   * FRESHNESS — only render blocks whose availability marker is true.
   * availability field: available_sqm
   * A block is "available" only if `block.available_sqm` is a positive number.
   * Sold / out-of-stock blocks (available_sqm == 0, null, or NaN) are filtered
   * out before they reach the DOM. This is the SAME field the existing
   * blockCardMarkup and renderInlineStoneBlocks paths read.
   */
  function isBlockAvailable(block) {
    if (!block) return false;
    var sqm = Number(block.available_sqm);
    return !isNaN(sqm) && sqm > 0;
  }

  function filterAvailableBlocks(blocks) {
    var out = [];
    for (var i = 0; i < (blocks || []).length; i++) {
      if (isBlockAvailable(blocks[i])) out.push(blocks[i]);
    }
    return out;
  }

  /**
   * The OUTER container carries NO CSS class — deliberately. The theme's
   * stylesheet gives `.block-search-panel` an author-origin `display: grid`
   * (component-predictive-search.css, loaded unconditionally), and an author
   * `display` beats the UA's `[hidden] { display: none }` rule — a classed
   * container can never actually hide. The rendered markup INSIDE the
   * container (renderSubmitResults / renderBlockList) already brings its own
   * `.block-search-panel` / `--scroll` wrappers, so styling is preserved and
   * we still invent no new classes.
   */
  function getOrCreateSubmitPanel(form) {
    var host = (form && form.parentNode) || document.body;
    var existing = host.querySelector
      ? host.querySelector('[data-emperor-submit-panel]')
      : null;
    if (existing) return existing;
    var panel = document.createElement('div');
    panel.setAttribute('data-emperor-submit-panel', '');
    panel.hidden = true;
    host.appendChild(panel);
    return panel;
  }

  /**
   * PANEL LIFECYCLE — a rendered submit panel is stale the moment the shopper
   * types again or presses Escape. Hide (never destroy) the existing panel;
   * the next submit re-renders into the same node. Deliberately minimal: no
   * new state machine, and nothing is created if no panel exists yet.
   */
  function hideSubmitPanel(form) {
    var host = form && form.parentNode;
    if (!host || !host.querySelector) return;
    var panel = host.querySelector('[data-emperor-submit-panel]');
    if (panel) panel.hidden = true;
  }

  /**
   * Dispatch a native submit on the given form, bypassing our own listener.
   * MUST be HTMLFormElement.prototype.submit — unlike requestSubmit(),
   * submit() does not re-fire the `submit` event, so the interception
   * listener cannot re-enter and loop when the cache is empty/errored
   * (exactly the case this fail-open path exists for). Called via the
   * prototype so a child control named "submit" cannot shadow the method.
   */
  function failOpenNativeSubmit(form) {
    if (!form) return;
    HTMLFormElement.prototype.submit.call(form);
  }

  /**
   * Render candidates into the submit panel. Honors the block-code deep-link
   * contract (handleMatch → window.location.assign(product + #block-hash))
   * and the multi-match contract (renderBlockList). Free-text queries get
   * a sectioned view: matched stones, then the available blocks grid.
   */
  function renderSubmitResults(panel, query, candidates) {
    var stones = candidates && candidates.stones ? candidates.stones : [];
    var rawBlocks = candidates && candidates.blocks ? candidates.blocks : [];
    var mode = candidates && candidates.mode ? candidates.mode : 'empty';

    // FRESHNESS filter — availability field: available_sqm
    var availableBlocks = filterAvailableBlocks(rawBlocks);

    if (mode === 'block-code' && candidates.data) {
      // The block-code path MIRRORS interceptSearch and does NOT filter on
      // availability: a block that exists but is sold must still resolve to
      // its stone — the real stock state is shown on the product page.
      // Telling a shopper "No stone matches block code X" for a code we DO
      // hold is a lie, and it re-opens the exact dead-end this file exists to
      // close. The freshness filter is for the free-text grid ONLY.
      var data = candidates.data;
      if (data.matched && rawBlocks.length === 1) {
        // Single block-code match — deep-link to product + #block hash
        // (paired with stone-block-grid.js revealHashBlock). Preserved
        // exactly as the dropdown / handleMatch path does it today.
        handleMatch(query, rawBlocks[0]);
        return;
      }
      if (data.matched && rawBlocks.length > 1) {
        renderBlockList(panel, query, rawBlocks);
        return;
      }
      renderNoMatch(panel, query);
      return;
    }

    if (!stones.length && !availableBlocks.length) {
      // Defensive only: the free-text zero-match case fails open in
      // handleSubmitFromCache before reaching this renderer.
      renderNoMatch(panel, query);
      return;
    }

    var stoneRows = stones.map(stoneRowMarkup).join('');
    // For free-text submits we link blocks back to their stone's product
    // page; resolveBlockUrl is async and would re-introduce a network call,
    // so for the instant-render path we use the safe fallback URL and let
    // the block-code deep-link path (above) handle the exact-product routing.
    var blockCards = availableBlocks
      .map(function (block) {
        return blockCardMarkup(block, fallbackStoneSearchUrl(block.stone_name || query));
      })
      .join('');

    var body = '';
    if (stoneRows) {
      body += sectionMarkup(
        'Stones',
        '<div class="block-search-stones" role="list">' + stoneRows + '</div>',
        'block-search-section--stones'
      );
    }
    if (blockCards) {
      body += sectionMarkup(
        'Available blocks',
        '<div class="block-search-panel block-search-panel--scroll" role="list">' +
          blockCards +
          '</div>',
        'block-search-section--blocks'
      );
    }
    panel.innerHTML = body;
    panel.hidden = false;
  }

  /**
   * Fail-open entrypoint for the submit path. Called by all three listeners.
   * Wrapped in try/catch at every level; on any failure we re-dispatch to
   * the native handler so the shopper is never stranded.
   */
  function handleSubmitFromCache(form) {
    if (!EMPEROR_SEARCH_FAIL_OPEN) {
      // Defensive: listeners check the kill-switch BEFORE preventDefault,
      // so we should never get here with the switch off — but if we do,
      // re-dispatch natively rather than leaving a dead search box.
      failOpenNativeSubmit(form);
      return;
    }
    var input = null;
    var q = '';
    try {
      input = form.querySelector('input[type="search"], input[name="q"]');
      q = input ? String(input.value || '').trim() : '';
    } catch (readErr) {
      // Could not read the input — fail open.
      failOpenNativeSubmit(form);
      return;
    }
    if (!q) {
      // Empty query — nothing to intercept; re-dispatch the native submit
      // (our listener already called preventDefault).
      failOpenNativeSubmit(form);
      return;
    }

    var panel = null;
    try {
      panel = getOrCreateSubmitPanel(form);
    } catch (panelErr) {
      failOpenNativeSubmit(form);
      return;
    }

    var candidatesPromise = null;
    try {
      // A synchronous throw here would escape AFTER preventDefault already
      // ran and leave a dead search box — the async .catch below only covers
      // rejection, not sync throws.
      candidatesPromise = getCandidateResultsForQuery(q);
    } catch (syncErr) {
      failOpenNativeSubmit(form);
      return;
    }

    candidatesPromise
      .then(function (candidates) {
        try {
          if (
            !candidates ||
            candidates.mode === 'empty' ||
            candidates.mode === 'stones-empty' ||
            candidates.mode === 'block-code-unresolved'
          ) {
            // Cache is empty / not-yet-loaded / unresolved — fail open.
            failOpenNativeSubmit(form);
            return;
          }
          if (
            candidates.mode === 'stones' &&
            !(candidates.stones || []).length &&
            !filterAvailableBlocks(candidates.blocks).length
          ) {
            // Free text matching no stone (e.g. "table"): native /search
            // also covers products, pages and articles beyond FMM stones —
            // fail open instead of dead-ending on a no-match panel.
            // (Block-code no-match stays an honest no-match panel: that IS
            // the 18010 -> White Onyx fix.)
            failOpenNativeSubmit(form);
            return;
          }
          renderSubmitResults(panel, q, candidates);
        } catch (renderErr) {
          failOpenNativeSubmit(form);
        }
      })
      .catch(function () {
        // Any error in the candidate pipeline — fail open.
        failOpenNativeSubmit(form);
      });
  }

  /**
   * Attach the three submit-intercept listeners to a form. Idempotent.
   * All three check the kill-switch BEFORE preventDefault and route through
   * handleSubmitFromCache (listener (a) may also yield to wireForm — see the
   * DOUBLE-WIRING GUARD below).
   */
  function wireSubmitInterception(form) {
    if (!form || isEmperorInlineSearchForm(form) || form.__emperorSubmitInterceptionWired) return;
    form.__emperorSubmitInterceptionWired = true;

    // (a) Form `submit` event — preventDefault + route through cache.
    form.addEventListener('submit', function (e) {
      if (!EMPEROR_SEARCH_FAIL_OPEN) return; // kill-switch: native submit untouched
      // DOUBLE-WIRING GUARD: on a form that autoWireForms() already wired, the
      // older wireForm listener OWNS block codes. On a real `submit` event both
      // listeners fire, so yield to it here — otherwise one query triggers two
      // lookups and paints two panels. Defence-in-depth: a genuine submit
      // event still reaches the form via programmatic requestSubmit(), a
      // soft-keyboard/IME submission that bypasses our keydown listener, or
      // future markup drift around the button.
      // Paths (b)/(c) below call handleSubmitFromCache directly: no submit
      // event is dispatched there, wireForm never runs, no double fire.
      var submittedInput = form.querySelector('input[type="search"], input[name="q"]');
      var submittedQuery = submittedInput ? String(submittedInput.value || '').trim() : '';
      if (form.__emperorBlockSearchWired && isBlockCodeLike(submittedQuery)) return;
      e.preventDefault();
      handleSubmitFromCache(form);
    });

    var input = form.querySelector('input[type="search"], input[name="q"]');
    if (input && !input.__emperorSubmitEnterWired) {
      input.__emperorSubmitEnterWired = true;
      // (b) Enter key on the search input — keydown to match the dropdown's
      //     key event style (the predictive component also listens on
      //     keydown for its own open/close logic; we mirror that).
      input.addEventListener('keydown', function (e) {
        // Escape dismisses a stale panel. Checked BEFORE (and separately from)
        // the Enter branch, and it does NOT preventDefault — the theme's own
        // Escape handling (closing the predictive dropdown) must still run.
        if (e.key === 'Escape') {
          hideSubmitPanel(form);
          return;
        }
        if (e.key !== 'Enter' && e.keyCode !== 13) return;
        if (!EMPEROR_SEARCH_FAIL_OPEN) return; // kill-switch: native Enter behaviour untouched
        e.preventDefault();
        handleSubmitFromCache(form);
      });

      // Typing invalidates whatever the last submit rendered — hide the panel
      // so results never outlive the query that produced them.
      input.addEventListener('input', function () {
        hideSubmitPanel(form);
      });
    }

    // (c) Click on the search/submit button (the visible magnifying-glass
    //     inside the same form). preventDefault so the form does not
    //     navigate; the click is otherwise redundant with the submit
    //     listener, but we attach it explicitly to guarantee the page
    //     never reloads even if a future theme wraps the button in a
    //     non-submit element.
    //     LOCALE NOTE: all four of the theme's search forms render their button
    //     WITHOUT a type attribute, and their aria-label is translated. A
    //     type-less <button> inside a form IS a submit button by spec, so
    //     `button:not([type])` catches them in every locale; the aria-label
    //     selector stays only as an EN-language belt-and-braces.
    var submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"], button:not([type]), button[aria-label*="search" i]'
    );
    if (submitButton && !submitButton.__emperorSubmitClickWired) {
      submitButton.__emperorSubmitClickWired = true;
      submitButton.addEventListener('click', function (e) {
        if (!EMPEROR_SEARCH_FAIL_OPEN) return; // kill-switch: native click behaviour untouched
        e.preventDefault();
        handleSubmitFromCache(form);
      });
    }
  }

  function autoWireSubmitInterception() {
    var forms = document.querySelectorAll(
      'form[data-block-search], predictive-search form[action*="/search"], form[action*="/search"]'
    );
    for (var i = 0; i < forms.length; i++) {
      if (!isEmperorInlineSearchForm(forms[i])) wireSubmitInterception(forms[i]);
    }
  }

  function patchPredictiveSearch() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('predictive-search').then(function () {
      var Ctor = customElements.get('predictive-search');
      if (!Ctor || !Ctor.prototype || Ctor.prototype.__emperorBlockSearchPatched) return;
      Ctor.prototype.__emperorBlockSearchPatched = true;

      var originalOnChange = Ctor.prototype.onChange;
      Ctor.prototype.onChange = function () {
        originalOnChange.apply(this, arguments);
        runPredictiveInventorySearch(this, this.getQuery());
      };

      var originalOnFocus = Ctor.prototype.onFocus;
      Ctor.prototype.onFocus = function () {
        originalOnFocus.apply(this, arguments);
        ensureStoneTypes();
        runPredictiveInventorySearch(this, this.getQuery ? this.getQuery() : '');
      };

      var originalRenderSearchResults = Ctor.prototype.renderSearchResults;
      Ctor.prototype.renderSearchResults = function () {
        originalRenderSearchResults.apply(this, arguments);
        runPredictiveInventorySearch(this, this.getQuery());
      };

      var originalCloseResults = Ctor.prototype.closeResults;
      Ctor.prototype.closeResults = function () {
        clearPredictiveInventoryResults(this);
        originalCloseResults.apply(this, arguments);
      };
    });
  }

  function init() {
    autoWireResultsPage();
    autoWireForms();
    autoWireSubmitInterception();
    patchPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
